import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import ButtonItem from '../components/ButtonItem';
import { PlusCircle, X } from 'lucide-react';

const TYPES = ['CD', 'LP', '굿즈'];
const EMPTY_FORM = { albumId: '', type: 'CD', barcode: '', label: '', returnDeposit: '', supplyPrice: '', stock: '', feeRate: '' };

function PhysicalAlbumWritePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const page = params.get('page') || 1;

  const [form, setForm] = useState(EMPTY_FORM);
  const [albumSearch, setAlbumSearch] = useState('');
  const [albumResults, setAlbumResults] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [saving, setSaving] = useState(false);

  // 신규 앨범 등록 모드
  const [newAlbumMode, setNewAlbumMode] = useState(false);
  const [newAlbumForm, setNewAlbumForm] = useState({ nameKr: '', artistId: '' });
  const [artistSearch, setArtistSearch] = useState('');
  const [artistResults, setArtistResults] = useState([]);
  const [selectedArtist, setSelectedArtist] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!albumSearch || selectedAlbum || newAlbumMode) return setAlbumResults([]);
      axios.get('/album', { params: { search: albumSearch, page: 1, limit: 10 } })
        .then((res) => setAlbumResults(res.data.items || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [albumSearch, selectedAlbum, newAlbumMode]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!artistSearch || selectedArtist) return setArtistResults([]);
      axios.get('/artist', { params: { search: artistSearch, page: 1, limit: 10 } })
        .then((res) => setArtistResults(res.data.items || []))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [artistSearch, selectedArtist]);

  const f = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const handleSave = async () => {
    if (!form.albumId) return alert('앨범을 선택해주세요.');
    setSaving(true);
    try {
      await axios.post('/api/physical-albums', form);
      navigate(`/PhysicalAlbums?page=${page}`);
    } catch (e) {
      alert(e.response?.data?.errorMessage || '저장 오류');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAlbumAndSave = async () => {
    if (!newAlbumForm.nameKr.trim()) return alert('앨범명을 입력해주세요.');
    if (!newAlbumForm.artistId) return alert('아티스트를 선택해주세요.');
    setSaving(true);
    try {
      // 음반 전용 앨범 신규 생성 (digitalYn='N')
      const { data: newAlbum } = await axios.post('/album', {
        nameKr: newAlbumForm.nameKr.trim(),
        artistId: newAlbumForm.artistId,
        digitalYn: 'N',
      });
      // 생성된 albumId로 음반 등록
      await axios.post('/api/physical-albums', { ...form, albumId: newAlbum.id });
      navigate(`/PhysicalAlbums?page=${page}`);
    } catch (e) {
      alert(e.response?.data?.errorMessage || e.response?.data?.message || '저장 오류');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.03] text-slate-200 focus:outline-none focus:border-sky-500';

  return (
    <>
      <div className="flex mb-8 justify-center items-center">
        <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">음반 등록</div>
      </div>

      <div className="space-y-6 max-w-2xl mx-auto">

        {/* 앨범 선택 */}
        <div>
          <label className="block font-semibold mb-1">앨범 <span className="text-rose-400">*</span></label>

          {!newAlbumMode ? (
            <>
              <input
                value={albumSearch}
                onChange={(e) => { setAlbumSearch(e.target.value); setSelectedAlbum(null); setForm((p) => ({ ...p, albumId: '' })); }}
                placeholder="앨범명 검색..."
                className={inputCls}
              />
              {albumResults.length > 0 && !selectedAlbum && (
                <div className="mt-1 bg-[#1a2740] border border-white/10 rounded-xl overflow-hidden max-h-48 overflow-y-auto">
                  {albumResults.map((a) => (
                    <button key={a.id}
                      onClick={() => { setSelectedAlbum(a); setAlbumSearch(a.nameKr); setForm((p) => ({ ...p, albumId: a.id })); setAlbumResults([]); }}
                      className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-white/10 flex items-center gap-2">
                      {a.image && <img src={a.image} className="w-6 h-6 rounded object-cover" alt="" />}
                      <span>{a.nameKr}</span>
                      <span className="text-slate-500 text-xs ml-auto">{a.artist?.nameKr}</span>
                    </button>
                  ))}
                </div>
              )}
              {selectedAlbum && <div className="mt-1 text-xs text-sky-400">{selectedAlbum.nameKr} 선택됨</div>}

              {/* 검색어 있지만 결과 없을 때 신규 등록 유도 */}
              {albumSearch && albumResults.length === 0 && !selectedAlbum && (
                <button
                  type="button"
                  onClick={() => { setNewAlbumMode(true); setNewAlbumForm({ nameKr: albumSearch, artistId: '' }); setAlbumResults([]); }}
                  className="mt-2 flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 transition-colors">
                  <PlusCircle size={15} />
                  음원 없는 음반 전용 앨범으로 신규 등록
                </button>
              )}
            </>
          ) : (
            /* 신규 앨범 입력 폼 */
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 space-y-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wide">음반 전용 앨범 신규 등록</span>
                <button type="button" onClick={() => { setNewAlbumMode(false); setNewAlbumForm({ nameKr: '', artistId: '' }); setSelectedArtist(null); setArtistSearch(''); }}
                  className="text-slate-500 hover:text-slate-300 transition-colors"><X size={15} /></button>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">앨범명 *</label>
                <input value={newAlbumForm.nameKr}
                  onChange={(e) => setNewAlbumForm(p => ({ ...p, nameKr: e.target.value }))}
                  placeholder="앨범명" className={inputCls} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-1">아티스트 *</label>
                <input value={artistSearch}
                  onChange={(e) => { setArtistSearch(e.target.value); setSelectedArtist(null); setNewAlbumForm(p => ({ ...p, artistId: '' })); }}
                  placeholder="아티스트 검색..." className={inputCls} />
                {artistResults.length > 0 && !selectedArtist && (
                  <div className="mt-1 bg-[#1a2740] border border-white/10 rounded-xl overflow-hidden max-h-36 overflow-y-auto">
                    {artistResults.map((a) => (
                      <button key={a.id}
                        onClick={() => { setSelectedArtist(a); setArtistSearch(a.nameKr); setNewAlbumForm(p => ({ ...p, artistId: a.id })); setArtistResults([]); }}
                        className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-white/10">
                        {a.nameKr}
                      </button>
                    ))}
                  </div>
                )}
                {selectedArtist && <div className="mt-1 text-xs text-sky-400">{selectedArtist.nameKr} 선택됨</div>}
              </div>
            </div>
          )}
        </div>

        {/* 분류 */}
        <div>
          <label className="block font-semibold mb-1">분류 <span className="text-rose-400">*</span></label>
          <div className="flex gap-2">
            {TYPES.map((t) => (
              <button key={t} onClick={() => setForm((p) => ({ ...p, type: t }))}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${form.type === t ? 'bg-sky-600 border-sky-500 text-white' : 'bg-white/5 border-white/10 text-slate-300 hover:border-white/20'}`}>
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* 기획사 / 바코드 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold mb-1">기획사</label>
            <input value={form.label} onChange={f('label')} placeholder="기획사" className={inputCls} />
          </div>
          <div>
            <label className="block font-semibold mb-1">바코드</label>
            <input value={form.barcode} onChange={f('barcode')} placeholder="바코드" className={inputCls} />
          </div>
        </div>

        {/* 출고가 / 반품예치금 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold mb-1">출고가 (원)</label>
            <input type="number" value={form.supplyPrice} onChange={f('supplyPrice')} placeholder="0" className={inputCls} />
          </div>
          <div>
            <label className="block font-semibold mb-1">반품예치금 (원)</label>
            <input type="number" value={form.returnDeposit} onChange={f('returnDeposit')} placeholder="0" className={inputCls} />
          </div>
        </div>

        {/* 초기재고 / 수수료율 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold mb-1">초기 재고</label>
            <input type="number" value={form.stock} onChange={f('stock')} placeholder="0" className={inputCls} />
          </div>
          <div>
            <label className="block font-semibold mb-1">유통수수료율 (%)</label>
            <input type="number" value={form.feeRate} onChange={f('feeRate')} placeholder="0" className={inputCls} />
          </div>
        </div>

      </div>

      <div className="w-full flex justify-end items-center mt-10 gap-2">
        <ButtonItem text="목록" color="gray" onClick={() => navigate(`/PhysicalAlbums?page=${page}`)} />
        <ButtonItem text="등록" color="blue"
          onClick={newAlbumMode ? handleCreateAlbumAndSave : handleSave}
          disabled={saving} />
      </div>
    </>
  );
}

export default PhysicalAlbumWritePage;
